import { initializeApp, getApp, getApps } from "firebase/app";
import {
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  browserLocalPersistence,
  connectAuthEmulator,
} from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAI, getGenerativeModel, GoogleAIBackend } from "firebase/ai";
import { Capacitor } from "@capacitor/core";
import { FirebaseCrashlytics } from "@capacitor-firebase/crashlytics";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { fetchRemoteConfigApiKeys, initRemoteConfig } from "../utils/remoteConfig";
import {
  USE_EMULATOR,
  EMULATOR_HOST,
  EMULATOR_PORTS,
} from "../config/emulator";

const geminiModelName = "gemini-2.5-flash";

// Your web app's Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  projectId: USE_EMULATOR
    ? (process.env.FIREBASE_EMULATOR_PROJECT_ID || "demo-activitylink")
    : process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID,
};

// Initialize Firebase only once to prevent errors in hot-reload environments.
const apps = getApps();
let app: any = null;
let auth: any = null;

// 1. Add this helper array for better reliability on Android
const persistenceProvider = Capacitor.isNativePlatform()
  ? [indexedDBLocalPersistence, browserLocalPersistence]
  : [browserLocalPersistence]; // Default for web, or use indexedDB if preferred

if (apps.length === 0) {
  if (firebaseConfig.apiKey && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    // 2. Use the array-based persistence for ALL platforms here
    // This is more robust than calling getAuth() later without config
    auth = initializeAuth(app, {
      persistence: persistenceProvider,
    });
  } else {
    console.error(
      "ERROR: ❌ Firebase configuration is missing required environment variables",
    );
  }
} else {
  app = apps[0];
  auth = getAuth(app);
}

async function getApiKeysInternal() {
  try {
    const keys = await fetchRemoteConfigApiKeys(app);
    return {
      GOOGLE_API_KEY: keys.GOOGLE_API_KEY,
      TMDB_API_KEY: keys.TMDB_API_KEY,
      UNSPLASH_ACCESS_KEY: keys.UNSPLASH_ACCESS_KEY,
      CATEGORY_IMAGES: keys.CATEGORY_IMAGES,
    };
  } catch (e) {
    crashlytics.logError(
      "Failed to fetch API keys from Remote Config",
      "getApiKeys",
      e,
    );
    throw e;
  }
}

// Cache API keys to avoid multiple fetches
let apiKeysPromise: Promise<{
  GOOGLE_API_KEY: string;
  TMDB_API_KEY: string;
  UNSPLASH_ACCESS_KEY: string;
  CATEGORY_IMAGES: Record<string, string[]>;
}> | null = null;

async function getApiKeys() {
  if (!apiKeysPromise) apiKeysPromise = getApiKeysInternal();
  return apiKeysPromise;
}

const db = app ? getFirestore(app) : null;
const storage = app ? getStorage(app) : null;
const ai = app ? getAI(app, { backend: new GoogleAIBackend() }) : null;

// ============================================================================
// EMULATOR CONFIGURATION
// ============================================================================
console.log("🔍 Emulator Check:", {
  USE_EMULATOR,
  USE_FIREBASE_EMULATOR: process.env.USE_FIREBASE_EMULATOR,
  hostname: window.location.hostname,
  hasApp: !!app,
  hasAuth: !!auth,
  hasDb: !!db,
  hasStorage: !!storage,
});

if (USE_EMULATOR && app && auth && db && storage) {
  console.log("🔧 Connecting to Firebase Emulators...");

  // Connect to Auth Emulator
  try {
    connectAuthEmulator(
      auth,
      `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`,
      { disableWarnings: true },
    );
    console.log(
      `✅ Auth Emulator: http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`,
    );
  } catch (error: any) {
    console.warn("⚠️ Auth emulator connection:", error?.message || error);
  }

  // Connect to Firestore Emulator
  try {
    connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
    console.log(
      `✅ Firestore Emulator: http://${EMULATOR_HOST}:${EMULATOR_PORTS.firestore}`,
    );
  } catch (error: any) {
    console.warn("⚠️ Firestore emulator connection:", error?.message || error);
  }

  // Connect to Storage Emulator
  try {
    connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
    console.log(
      `✅ Storage Emulator: http://${EMULATOR_HOST}:${EMULATOR_PORTS.storage}`,
    );
  } catch (error: any) {
    console.warn("⚠️ Storage emulator connection:", error?.message || error);
  }

  console.log(`
📡 Firebase Emulators Connected!
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌐 Emulator UI:    http://localhost:${EMULATOR_PORTS.ui}
🔐 Auth:           http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}
📄 Firestore:      http://${EMULATOR_HOST}:${EMULATOR_PORTS.firestore}
💾 Storage:        http://${EMULATOR_HOST}:${EMULATOR_PORTS.storage}
⚡ Functions:      http://${EMULATOR_HOST}:${EMULATOR_PORTS.functions}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  `);
}
// ============================================================================

// Initialize Crashlytics for native platforms
if (Capacitor.isNativePlatform()) {
  FirebaseCrashlytics.setEnabled({ enabled: true }).catch(console.error);

  // Set up global error handler for uncaught errors
  window.addEventListener("error", (event) => {
    FirebaseCrashlytics.recordException({
      message: event.message,
      stacktrace: event.error?.stack || "No stack trace available",
    }).catch(console.error);
  });

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    FirebaseCrashlytics.recordException({
      message: `Unhandled Promise Rejection: ${event.reason}`,
      stacktrace: event.reason?.stack || String(event.reason),
    }).catch(console.error);
  });
}

// Initialize Analytics for native platforms
if (Capacitor.isNativePlatform()) {
  FirebaseAnalytics.setEnabled({ enabled: true }).catch(console.error);
}

// Enable offline persistence for mobile
if (db && typeof window !== "undefined") {
  // Enable offline persistence
  import("firebase/firestore").then((firestore) => {
    firestore.enableNetwork(db).catch((error) => {
      console.warn("Failed to enable Firestore network:", error);
    });
  });
}

const enableLog = true; // Set to true to enable logging of analytics and crashlytics calls

// Crashlytics utility functions
export const crashlytics = {
  /**
   * Log a non-fatal error to Crashlytics
   */
  logError: async (message: string, context?: string, error?: any) => {
    message = message || "Unknown error";
    let m = context ? `${context}: ${message}` : message;
    let isNative = Capacitor.isNativePlatform();
    // Prepare the stack frames
    let stackFrames: any[] = [];
    if (error instanceof Error && error.stack) {
      // Basic parsing of the stack string into frames
      stackFrames = error.stack
        .split("\n")
        .slice(1)
        .map((line) => ({
          functionName: line.trim() || "unknown",
          fileName: line.includes("(")
            ? line.split("(")[1].split(")")[0]
            : line.trim(),
        }));
    }
    console.log(
      `📡crashlytics:logError:${isNative?"native:" : ""}${m}`,
      stackFrames,
    );
    if (!isNative) return;
    try {
      await FirebaseCrashlytics.recordException({
        message: m,
        stacktrace: stackFrames,
      });
    } catch (e) {
      console.error("📡crashlytics:Failed to log error to Crashlytics:", e);
    }
  },

  /**
   * Set user identifier for crash reports
   */
  setUserId: async (userId: string) => {
    if (enableLog) {
      console.log("📡crashlytics:setUserId:", userId);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await FirebaseCrashlytics.setUserId({ userId });
    } catch (e) {
      console.error("📡Failed to set Crashlytics user ID:", e);
    }
  },

  /**
   * Add custom key-value pair for crash context
   */
  setCustomKey: async (key: string, value: string) => {
    if (enableLog) {
      console.log("📡crashlytics:setCustomKey:", key, value);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      // Infer type for value
      let type: "string" | "long" | "double" | "boolean" | "int" | "float" =
        "string";
      if (typeof value === "boolean") type = "boolean";
      else if (typeof value === "number")
        type = Number.isInteger(value) ? "int" : "double";
      await FirebaseCrashlytics.setCustomKey({ key, value, type });
    } catch (e) {
      console.error("📡crashlytics:Failed to set Crashlytics custom key:", e);
    }
  },

  /**
   * Log a custom message
   */
  log: async (message: string) => {
    if (enableLog) {
      console.log("📡crashlytics:log:", message);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await FirebaseCrashlytics.log({ message });
    } catch (e) {
      console.error("📡Failed to log to Crashlytics:", e);
    }
  },
};

// Analytics utility functions
export const analytics = {
  /**
   * Log a custom event
   */
  logEvent: async (name: string, params?: Record<string, any>) => {
    if (enableLog) {
      console.log(`📥analytics:logEvent: ${name} params: ${JSON.stringify(params)}`);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await FirebaseAnalytics.logEvent({ name, params: params || {} });
    } catch (e) {
      console.error("📥analytics:Failed to log analytics event:", e);
    }
  },

  /**
   * Set user ID for analytics
   */
  setUserId: async (userId: string) => {
    if (enableLog) {
      console.log("📥analytics:setUserId:", userId);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await FirebaseAnalytics.setUserId({ userId });
    } catch (e) {
      console.error("📥analytics:Failed to set analytics user ID:", e);
    }
  },

  /**
   * Set user property
   */
  setUserProperty: async (key: string, value: string) => {
    if (enableLog) {
      console.log("📥analytics:setUserProperty:", key, value);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await FirebaseAnalytics.setUserProperty({ key, value });
    } catch (e) {
      console.error("Failed to set analytics user property:", e);
    }
  },

  /**
   * Set current screen name
   */
  setCurrentScreen: async (screenName: string, screenClass?: string) => {
    if (enableLog) {
      console.log("📥analytics:setCurrentScreen:", screenName, screenClass);
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await FirebaseAnalytics.setCurrentScreen({
        screenName,
        screenClassOverride: screenClass,
      });
    } catch (e) {
      console.error("📥Failed to set current screen:", e);
    }
  },
};

// Fire-and-forget Remote Config fetch so CATEGORY_IMAGES is populated early
initRemoteConfig(app).catch((err) => {
  console.warn("Remote Config preload failed", err);
});

export {
  app,
  auth,
  db,
  storage,
  geminiModelName,
  ai,
  getGenerativeModel,
  getApiKeys,
};
